import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  CloseIcon,
  Input,
  Paragrapgh,
  Subtitle,
  Title,
  Dropdown as Select,
} from '../../components/shared';
import { updateAccountSetting } from '../../store/auth/auth.actions';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { selectCustomTasks } from '../../store/tasks/tasks.selector';
import { ITaskSetting } from '../../entities/user.entities';
import { Modal, Table } from 'antd';
import ModalFeedbackView from '../../components/shared/feedback/ModalFeedbackView';
import DotsMenu from '../../components/shared/dropdown-menu/DotsMenu';
import CustomSelect from '../../components/shared/input/CustomSelect';
import { selectIsAdmin } from '../../store/auth/auth.selector';

const legalOptions = [
  { id: 'SEEDING', value: 'SEEDING', label: 'Seeding' },
  {
    id: 'ASSESSMENT',
    value: 'ASSESSMENT',
    label: 'Assessment',
  },
  {
    id: 'MAINTENANCE',
    value: 'MAINTENANCE',
    label: 'Maintenance',
  },
  {
    id: 'INVENTORY_SEED',
    value: 'INVENTORY_SEED',
    label: 'Inventory management',
  },
  { id: 'HARVEST', value: 'HARVEST', label: 'Harvest' },
  { id: 'TAGGING', value: 'TAGGING', label: 'Tagging' },
];

const isLegalAction = (v: string) => legalOptions.some(x => x.value === v);

interface Props {
  visible: boolean;
  onClose: () => void;
  data?: ITaskSetting;
}

const TaskSettingModal = ({ visible, onClose, data }: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const customTasks = useSelector(selectCustomTasks);
  const isAdmin = useSelector(selectIsAdmin);

  const [form, setForm] = useState<ITaskSetting>(
    data ?? { name: '', action: null },
  );
  const [disabled, setDisabled] = useState(false);

  const actionOptions = [
    { id: 'NONE', value: 'NONE', label: translate(lang, 'None') },
    ...legalOptions.map(x => ({ ...x, label: translate(lang, x.label) })),
    ...(customTasks
      ?.map(x => x.action as string)
      .filter(v => v && !isLegalAction(v) && v !== 'OTHER')
      .filter((i, j, k) => k.indexOf(i) === j)
      .map(x => ({ id: x, value: x, label: x })) ?? []),
  ];

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        message,
        type: 'error',
      }),
    );
  const confirmClick = async () => {
    if (!form.name) {
      showError(translate(lang, '_task_name_req'));
      return;
    }
    const tasks = data
      ? (customTasks?.map(x => (x.name === data.name ? form : x)) ?? [])
      : [...(customTasks ?? []), form];
    setDisabled(true);
    const r = await dispatch(updateAccountSetting({ custom_tasks: tasks }));
    setDisabled(false);
    if (r === true) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, 'Updated successfully'),
        }),
      );
      onClose();
    } else {
      showError(translate(lang, r));
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      closable
      closeIcon={<CloseIcon />}
      footer={null}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-16'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, data ? 'Edit task setting' : 'Add task setting')}
          </Subtitle>
        </div>
        <div className='mt-17 mb-17'>
          <Input
            label={translate(lang, 'Task name')}
            type='text'
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <CustomSelect
          className='mb-24'
          label={translate(lang, 'Action')}
          options={actionOptions}
          value={
            form.action === null || form.action === undefined
              ? 'NONE'
              : form.action
          }
          onChange={v =>
            setForm({
              ...form,
              action: v === 'NONE' || v === undefined ? null : v,
            })
          }
        />
      </div>
      <div className='modal-button d-flex justify-end align-center mt-32'>
        <Button
          width='small'
          size={2}
          type='fill'
          color='red'
          className='rsp-btn'
          onClick={onClose}
          disabled={disabled}
        >
          {translate(lang, 'Close')}
        </Button>
        <Button
          width='small'
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={confirmClick}
          disabled={disabled || !isAdmin}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

const TaskSettings = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const customTasks = useSelector(selectCustomTasks);
  const isAdmin = useSelector(selectIsAdmin);

  const [disabled, setDisabled] = useState(false);
  const [editItem, setEditItem] = useState<ITaskSetting>();
  const [visibleAdd, setVisibleAdd] = useState(false);

  const deleteItem = async (name: string) => {
    const tmp = customTasks?.filter(x => x.name !== name) ?? [];
    setDisabled(true);
    const r = await dispatch(updateAccountSetting({ custom_tasks: tmp }));
    setDisabled(false);
    if (r === true) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, 'Updated successfully'),
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, r),
        }),
      );
    }
  };

  const columns = [
    {
      title: translate(lang, 'Name'),
      key: 'name',
      render: (x: ITaskSetting) => <span>{x.name}</span>,
    },
    {
      title: translate(lang, 'Action'),
      key: 'action',
      render: (x: ITaskSetting) => (
        <span>
          {!!x.action && isLegalAction(x.action)
            ? translate(
                lang,
                legalOptions.find(l => l.value === x.action)?.label as string,
              )
            : x.action}
        </span>
      ),
    },
    {
      title: '',
      key: 'edit-remove',
      render: (x: ITaskSetting) =>
        isAdmin ? (
          <DotsMenu
            items={[
              {
                label: translate(lang, 'Edit'),
                onClick: () => setEditItem(x),
              },
              {
                label: translate(lang, 'Delete'),
                onClick: () => deleteItem(x.name),
              },
            ]}
          />
        ) : null,
    },
  ];

  return (
    <div className='content pb-32 task-settings'>
      <div className='mb-16 mt-16'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, 'Custom Tasks')}
        </Title>
        <div className='text-right'>
          <Button
            className='ml-17'
            type='fill'
            color='green'
            width='small'
            size={1}
            disabled={disabled || !isAdmin}
            onClick={() => setVisibleAdd(true)}
          >
            {translate(lang, 'Add')}
          </Button>
        </div>
      </div>
      <div className='mt-16'>
        {customTasks && customTasks.length > 0 ? (
          <Table
            className={'table table--units table--is__cursor'}
            rowKey={'name'}
            columns={columns}
            dataSource={customTasks}
            pagination={false}
          />
        ) : (
          <div>
            <Paragrapgh size={1} color='black-5' align='left' fontWeight={400}>
              {translate(lang, 'No data available')}
            </Paragrapgh>
          </div>
        )}
      </div>
      {visibleAdd && (
        <TaskSettingModal visible={true} onClose={() => setVisibleAdd(false)} />
      )}
      {editItem && (
        <TaskSettingModal
          visible={true}
          onClose={() => setEditItem(undefined)}
          data={editItem}
        />
      )}
    </div>
  );
};

export default TaskSettings;
